/**
 * Daily Brief Page (Client)
 *
 * CEO-first landing: renders the latest persisted Decision Console Daily Brief
 * for the active strategy. No mock data is used outside of E2E test mode.
 */

'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { ExecutionCockpitDailyBriefV6 } from '@/ui-golden/execution-cockpit';
import { dailyBriefV6Mock } from '@/ui-golden/execution-cockpit/mocks/dailyBriefV6.mock';
import {
  getLatestExecutionCockpitRun,
  getExecutionCockpitDailyBriefFromRun,
  ensureLatestExecutionCockpitRun,
  activateExecutionCockpitDecisionPackageForRun,
  saveExecutionCockpitDecisionPackageDraftForRun,
  startExecutionCockpitDecisionPackageAmendmentForRun,
  reviewExecutionCockpitDecisionPackageForRun,
  getExecutionCockpitDecisionPackageReviewHistoryForRun,
} from '@/lib/executionCockpitApi';
import {
  formatExecutiveOwnerLabel,
  formatExecutiveStrategyScopeLabel,
} from '@/lib/executive-safe-format';
import { listTenantMembers } from '@/lib/tenantAdminApi';
import { loadOperatingContext } from '@/lib/operating-context';
import type {
  ExecutionCockpitDailyBriefDto,
  ExecutionCockpitDailyBriefMetaDto,
  ExecutionCockpitDailyBriefViewDto,
  ExecutionCockpitDecisionPackageActivateRequestDto,
  ExecutionCockpitDecisionPackageDecisionDto,
  ExecutionCockpitDecisionPackageDto,
  ExecutionCockpitDecisionPackageReviewRequestDto,
} from '@helix/lib';
import { humanizeApiError } from '@/lib/humanizeApiError';
import { LinkButton } from '@/components/ui/link-button';
import { AskHelixLink } from '@/components/ask/AskHelixLink';

const LAST_STRATEGY_KEY = 'helix.dailyBrief.lastStrategyId';

type DecisionOwnerCandidate = { userId: string; label: string; email: string };

const isE2eMode =
  process.env.NEXT_PUBLIC_HELIX_E2E_TEST_MODE === 'true' || process.env.NEXT_PUBLIC_HELIX_E2E_MODE === 'true';

function buildDefaultView(strategyLabel: string | null): ExecutionCockpitDailyBriefViewDto {
  return {
    strategyLabel: strategyLabel ?? 'Strategy',
    timeWindowLabel: 'This Quarter',
    lensLabel: 'Company-wide',
    pinnedLenses: [
      { id: 'global', label: 'Global' },
      { id: 'americas', label: 'Americas' },
      { id: 'emea', label: 'EMEA' },
      { id: 'apac', label: 'APAC' },
    ],
    activePinnedLensId: 'global',
  };
}

function buildDeterministicBoardMemo(params: {
  dailyBrief: ExecutionCockpitDailyBriefDto;
  runId: string;
  meta?: ExecutionCockpitDailyBriefMetaDto | null;
  decisionPackage?: ExecutionCockpitDecisionPackageDto | null;
  decisionPackageDecisions?: ExecutionCockpitDecisionPackageDecisionDto[] | null;
  ownerCandidates?: DecisionOwnerCandidate[] | null;
}): string {
  const { dailyBrief, meta, decisionPackage, decisionPackageDecisions, ownerCandidates } = params;
  const fmt = (value: string | null | undefined) => String(value ?? '').trim();
  const fmtDate = (iso: string | null | undefined) => {
    const raw = String(iso ?? '').trim();
    if (!raw) return '—';
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    return date.toISOString().slice(0, 10);
  };

  const formatStructuredTargets = (
    targets: ExecutionCockpitDecisionPackageDecisionDto['targets'] | null | undefined,
  ): string | null => {
    if (!targets) return null;
    const parts: string[] = [];
    if (targets.targetRag) parts.push(`RAG ≥ ${String(targets.targetRag).toUpperCase()}`);
    if (targets.maxSlipWeeks != null) parts.push(`Slip ≤ ${targets.maxSlipWeeks}w`);
    if (targets.maxGapPoints != null) parts.push(`Gap ≤ ${targets.maxGapPoints} pts`);
    if (targets.maxStalledCount != null) parts.push(`Stalled ≤ ${targets.maxStalledCount}`);
    return parts.length ? parts.join(' • ') : null;
  };

  const computeNextReviewDueIso = (referenceAtIso: string, cadence: string | null | undefined): string | null => {
    const c = String(cadence ?? '').trim().toLowerCase();
    if (c !== 'weekly' && c !== 'biweekly' && c !== 'monthly') return null;
    const referenceAt = new Date(String(referenceAtIso ?? '').trim());
    if (Number.isNaN(referenceAt.getTime())) return null;

    const addDaysUtc = (date: Date, days: number): Date => {
      const out = new Date(date.getTime());
      out.setUTCDate(out.getUTCDate() + days);
      return out;
    };

    const addMonthsClampedUtc = (date: Date, months: number): Date => {
      const y = date.getUTCFullYear();
      const m = date.getUTCMonth();
      const d = date.getUTCDate();
      const h = date.getUTCHours();
      const min = date.getUTCMinutes();
      const s = date.getUTCSeconds();
      const ms = date.getUTCMilliseconds();

      const targetMonthIndex = m + months;
      const targetYear = y + Math.floor(targetMonthIndex / 12);
      const targetMonth = targetMonthIndex % 12;
      const daysInTargetMonth = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
      const clampedDay = Math.min(d, daysInTargetMonth);
      return new Date(Date.UTC(targetYear, targetMonth, clampedDay, h, min, s, ms));
    };

    const addInterval = (date: Date): Date => {
      if (c === 'weekly') return addDaysUtc(date, 7);
      if (c === 'biweekly') return addDaysUtc(date, 14);
      return addMonthsClampedUtc(date, 1);
    };

    return addInterval(referenceAt).toISOString();
  };

  const evaluateStructuredTargets = (
    targets: ExecutionCockpitDecisionPackageDecisionDto['targets'] | null | undefined,
    current: { rag: string | null; slipWeeks: number | null; gapPoints: number | null; stalledCount: number | null },
  ): 'Met' | 'Off-track' | 'Unknown' | 'Not set' => {
    const t = targets ?? null;
    const hasAny =
      t != null &&
      (Boolean(t.targetRag) || t.maxSlipWeeks != null || t.maxGapPoints != null || t.maxStalledCount != null);
    if (!hasAny) return 'Not set';

    const checks: Array<boolean | null> = [];
    const ragRank = (rag: string | null) => (rag === 'green' ? 3 : rag === 'amber' ? 2 : rag === 'red' ? 1 : 0);
    if (t?.targetRag) checks.push(current.rag == null ? null : ragRank(current.rag) >= ragRank(t.targetRag));
    if (t?.maxSlipWeeks != null) checks.push(current.slipWeeks == null ? null : current.slipWeeks <= t.maxSlipWeeks);
    if (t?.maxGapPoints != null) checks.push(current.gapPoints == null ? null : current.gapPoints <= t.maxGapPoints);
    if (t?.maxStalledCount != null)
      checks.push(current.stalledCount == null ? null : current.stalledCount <= t.maxStalledCount);

    if (checks.some((c) => c === false)) return 'Off-track';
    if (checks.some((c) => c == null)) return 'Unknown';
    return 'Met';
  };

  const ownerLabelById = new Map<string, string>();
  for (const m of ownerCandidates ?? []) {
    const userId = String(m.userId ?? '').trim();
    if (!userId) continue;
    const label = String(m.label ?? '').trim();
    const email = String(m.email ?? '').trim();
    ownerLabelById.set(userId, label && email ? `${label} (${email})` : label || email || userId);
  }

  const sourceKind = fmt(meta?.run?.sourceKind);
  const sourceLabel =
    sourceKind === 'jira_snapshot_artifact'
      ? 'Jira work-signal snapshot'
      : sourceKind === 'github_issues_snapshot'
        ? 'GitHub issue snapshot'
        : 'Approved operating snapshot';

  const decisionByKey = new Map<string, ExecutionCockpitDecisionPackageDecisionDto>();
  for (const d of decisionPackageDecisions ?? []) {
    const key = String(d.initiativeKey ?? '').trim();
    if (!key) continue;
    decisionByKey.set(key, d);
  }

  const betByKey = new Map(dailyBrief.bets.map((b) => [b.key, b]));

  const lines: string[] = [];
  lines.push(`# Helix Board Memo (Daily Brief)`);
  lines.push('');
  lines.push('- Evidence package: Approved Daily Brief run');
  lines.push(`- As-of: ${meta?.run?.computedAt ?? dailyBrief.asOf}`);
  lines.push(`- Strategy: ${formatExecutiveStrategyScopeLabel(meta?.run?.strategyId)}`);
  lines.push(`- Source: ${sourceLabel}`);
  lines.push('- Evidence trail: retained in Helix for audit replay');
  lines.push(`- Org baseline: ${meta?.run?.orgBaseline ? fmt(meta.run.orgBaseline.updatedAt) : '—'}`);
  lines.push('- Rule set: Approved Daily Brief rules');
  const stalledDays = meta?.run?.config?.stalledDaysThreshold;
  const lookbackDays = meta?.run?.config?.lookbackDaysForThroughput;
  lines.push(
    `- Stall threshold: ${
      Number.isFinite(Number(stalledDays)) ? `${Number(stalledDays)} days` : '—'
    }`,
  );
  lines.push(
    `- Throughput lookback: ${
      Number.isFinite(Number(lookbackDays)) ? `${Number(lookbackDays)} days` : '—'
    }`,
  );
  lines.push(
    `- Priority ordering: status → strategic value → slip (weeks) → gap points → stalled count → name`,
  );
  lines.push(
    `- Prior comparison: ${
      meta?.previousRun
        ? `As of ${fmtDate(meta.previousRun.computedAt)} (${meta.isComparableToPreviousRun ? 'comparable' : 'not comparable'})`
        : '—'
    }`,
  );
  {
    const evidence = Array.isArray(dailyBrief.evidenceCoverage) ? dailyBrief.evidenceCoverage : [];
    const connected = evidence.filter((e) => e.status === 'connected').length;
    const stale = evidence.filter((e) => e.status === 'stale').length;
    const notConnected = evidence.filter((e) => e.status === 'not_connected').length;
    lines.push(`- Evidence: ${connected} connected • ${stale} stale • ${notConnected} not connected`);

    const workSignals = evidence.find((e) => e.id === 'work_signals') ?? null;
    if (workSignals && workSignals.status !== 'connected') {
      const detail = fmt(workSignals.detail);
      lines.push(
        `- Evidence gap: Work signals ${fmt(workSignals.status)}${detail ? ` — ${detail}` : ''}`,
      );
    }

    const orgBaseline = evidence.find((e) => e.id === 'org_baseline') ?? null;
    const hasCapacityGapAssumptions = dailyBrief.bets.some((b) =>
      Array.isArray(b.priorityDrivers) ? b.priorityDrivers.some((d) => d.id === 'capacity_gap' && d.basis === 'assumption') : false,
    );
    if (orgBaseline && orgBaseline.status !== 'connected' && hasCapacityGapAssumptions) {
      lines.push(`- Evidence gap: Org baseline ${fmt(orgBaseline.status)} — capacity gaps may be assumptions`);
    }
  }
  lines.push(`- Strategy: ${fmt(dailyBrief.filters?.strategyLabel) || '—'}`);
  lines.push(`- Time window: ${fmt(dailyBrief.filters?.timeWindowLabel) || '—'}`);
  lines.push(`- Lens: ${fmt(dailyBrief.filters?.lensLabel) || '—'}`);
  lines.push('');

  lines.push('## Executive takeaway');
  lines.push(`- What will miss: ${fmt(dailyBrief.executiveVerdict?.whatWillMiss) || '—'}`);
  lines.push(`- Binding constraint: ${fmt(dailyBrief.executiveVerdict?.bindingConstraint) || '—'}`);
  lines.push(`- Recommendation: ${fmt(dailyBrief.executiveVerdict?.decisionThatChangesOutcome) || '—'}`);
  lines.push('');

  lines.push('## Decision Docket');
  if (decisionPackage) {
    const statusLabel =
      decisionPackage.status === 'active'
        ? `Active v${decisionPackage.version} (${fmtDate(decisionPackage.activatedAt)})`
        : `Draft v${decisionPackage.version} (not activated)`;
    lines.push(`- Decision package: ${statusLabel}`);
  } else {
    lines.push(`- Decision package: Not saved (recommendations only)`);
  }
  if (Array.isArray(dailyBrief.decisionDocket) && dailyBrief.decisionDocket.length) {
    for (const d of dailyBrief.decisionDocket) {
      lines.push(`- ${fmt(d.question) || 'Decision'}`);
      const initiativeKey = fmt(d.linkedInitiativeKey);
      const bet = initiativeKey ? betByKey.get(initiativeKey) ?? null : null;
      const betLabel = bet?.name ? `${bet.name} (${initiativeKey})` : initiativeKey || null;
      if (betLabel) lines.push(`  - Initiative: ${betLabel}`);

      const stored = initiativeKey ? decisionByKey.get(initiativeKey) ?? null : null;
      const selectedFromStored = stored?.selectedOption ?? null;
      const selectedFromIndex =
        stored?.selectedOptionIndex != null && bet ? bet.options[stored.selectedOptionIndex] ?? null : null;
      const selectedOption = selectedFromStored ?? selectedFromIndex;
      const recommendedOption = bet?.options?.[0] ?? null;

      if (decisionPackage) {
        if (selectedOption) {
          lines.push(`  - Selected: ${fmt(selectedOption.action) || '—'} (${fmt(selectedOption.delta) || '—'})`);
        } else if (recommendedOption) {
          lines.push(`  - Selected: — • Recommended: ${fmt(recommendedOption.action) || '—'} (${fmt(recommendedOption.delta) || '—'})`);
        } else {
          lines.push(`  - Selected: —`);
        }

        const ownerUserId = fmt(stored?.ownerUserId ?? null) || null;
        const ownerLabel = formatExecutiveOwnerLabel(ownerUserId, ownerLabelById);
        const dueLabel = stored?.dueAt ? fmtDate(stored.dueAt) : '—';
        lines.push(`  - Owner: ${ownerLabel} • Due: ${dueLabel}`);
        const cadenceRaw = fmt(stored?.reviewCadence ?? null) || '';
        const cadenceLabel =
          cadenceRaw.toLowerCase() === 'weekly'
            ? 'Weekly'
            : cadenceRaw.toLowerCase() === 'biweekly'
              ? 'Biweekly'
              : cadenceRaw.toLowerCase() === 'monthly'
                ? 'Monthly'
                : cadenceRaw || '—';
        lines.push(`  - Cadence: ${cadenceLabel}`);
        const structured = formatStructuredTargets(stored?.targets);
        const targetsText = fmt(stored?.successCriteria ?? null);
        if (structured) lines.push(`  - Targets (metrics): ${structured}`);
        if (targetsText) lines.push(`  - Targets (text): ${targetsText}`);
        if (!structured && !targetsText) lines.push(`  - Targets: —`);
        if (fmt(stored?.rationale ?? null)) lines.push(`  - Rationale: ${fmt(stored?.rationale ?? null)}`);
      } else if (recommendedOption) {
        lines.push(`  - Recommended: ${fmt(recommendedOption.action) || '—'} (${fmt(recommendedOption.delta) || '—'})`);
      }

      if (fmt(d.dueIn)) lines.push(`  - Due: ${fmt(d.dueIn)}`);
      if (fmt(d.urgency)) lines.push(`  - Urgency: ${fmt(d.urgency)}`);
      const tradeoffs = Array.isArray(d.tradeoffs) ? d.tradeoffs.map(fmt).filter(Boolean) : [];
      if (tradeoffs.length) {
        lines.push('  - Tradeoffs:');
        for (const t of tradeoffs.slice(0, 6)) lines.push(`    - ${t}`);
      }
    }
  } else {
    lines.push('- (No pending decisions)');
  }
  lines.push('');

  if (meta?.activationBaseline?.deltas?.length) {
    const baseline = meta.activationBaseline;
    lines.push('## Measurement (Since Last Activation)');
    lines.push(
      `- Baseline: ${fmt(baseline.baselineAsOf) || '—'} • Approved package v${baseline.version}`,
    );

    for (const row of baseline.deltas) {
      const betName = betByKey.get(row.initiativeKey)?.name ?? row.initiativeKey;
      const rag = (value: string | null | undefined) => (value ? String(value).toUpperCase() : '—');
      const num = (value: number | null | undefined) => (value == null ? '—' : String(value));
      const delta = (value: number | null | undefined) => {
        if (value == null) return '—';
        if (value === 0) return '0';
        return `${value > 0 ? '+' : ''}${value}`;
      };

      lines.push(`- ${betName}`);
      lines.push(`  - RAG: ${rag(row.baseline.rag)} → ${rag(row.current.rag)}`);
      const ownerUserId = fmt(row.decision?.ownerUserId ?? null) || null;
      const ownerLabel = formatExecutiveOwnerLabel(ownerUserId, ownerLabelById);
      const cadenceRaw = fmt(row.decision?.reviewCadence ?? null) || '';
      const cadenceLabel =
        cadenceRaw.toLowerCase() === 'weekly'
          ? 'Weekly'
          : cadenceRaw.toLowerCase() === 'biweekly'
            ? 'Biweekly'
            : cadenceRaw.toLowerCase() === 'monthly'
              ? 'Monthly'
              : cadenceRaw || '—';
      lines.push(`  - Owner: ${ownerLabel} • Cadence: ${cadenceLabel}`);
      const lastReviewedIso = fmt(row.decision?.lastReviewedAt ?? null) || null;
      const lastReviewedRunId = fmt(row.decision?.lastReviewedForRunId ?? null) || null;
      if (lastReviewedIso) {
        lines.push(
          `  - Last review: ${fmtDate(lastReviewedIso)}${lastReviewedRunId ? ` (run ${lastReviewedRunId})` : ''}`,
        );
      } else {
        lines.push(`  - Last review: Not logged`);
      }
      const outcomeRaw = fmt(row.decision?.lastReviewOutcome ?? null) || null;
      const outcomeLabel =
        outcomeRaw === 'continue'
          ? 'Continue'
          : outcomeRaw === 'escalate'
            ? 'Escalate'
            : outcomeRaw === 'rescope'
              ? 'Re-scope'
              : outcomeRaw === 'defer'
                ? 'Defer'
                : null;
      if (outcomeLabel) lines.push(`  - Last review outcome: ${outcomeLabel}`);
      if (row.decision?.lastReviewHasNotes === true) lines.push(`  - Last review notes: Logged (see audit trail)`);
      const referenceAt = lastReviewedIso ?? baseline.activatedAt;
      const nextReviewDueIso = computeNextReviewDueIso(referenceAt, row.decision?.reviewCadence ?? null);
      if (nextReviewDueIso) {
        lines.push(
          `  - Next review due: ${fmtDate(nextReviewDueIso)}${lastReviewedIso ? '' : ' (derived from activation)'}`,
        );
      }
      const structured = formatStructuredTargets(row.decision?.targets);
      const targetsText = fmt(row.decision?.successCriteria ?? null);
      if (structured) lines.push(`  - Targets (metrics): ${structured}`);
      if (targetsText) lines.push(`  - Targets (text): ${targetsText}`);
      lines.push(
        `  - Target status: ${evaluateStructuredTargets(row.decision?.targets, {
          rag: row.current.rag,
          slipWeeks: row.current.slipWeeks,
          gapPoints: row.current.gapPoints,
          stalledCount: row.current.stalledCount,
        })}`,
      );
      lines.push(
        `  - Slip (weeks): ${num(row.baseline.slipWeeks)} → ${num(row.current.slipWeeks)} (Δ ${delta(row.delta.slipWeeks)})`,
      );
      lines.push(
        `  - Gap (points): ${num(row.baseline.gapPoints)} → ${num(row.current.gapPoints)} (Δ ${delta(row.delta.gapPoints)})`,
      );
      lines.push(
        `  - Stalled count: ${num(row.baseline.stalledCount)} → ${num(row.current.stalledCount)} (Δ ${delta(row.delta.stalledCount)})`,
      );
    }

    lines.push('');
    lines.push('- Definition: Δ = current − baseline (negative is improvement).');
    lines.push('- Slip definition: max(0, ceil(weeksNeeded − weeksRemaining)).');
    lines.push('- Next review due is derived from last review log + cadence; when no review log exists, activation timestamp is the reference (explicitly labeled).');
    lines.push('- Structured targets evaluate current metrics; “Unknown” means a required metric is missing.');
    lines.push('');
  }

  lines.push('## Risk Radar');
  if (Array.isArray(dailyBrief.riskRadar) && dailyBrief.riskRadar.length) {
    for (const r of dailyBrief.riskRadar) {
      const deltaLabel = r.delta == null ? '—' : String(r.delta);
      lines.push(`- ${fmt(r.label) || r.id}: ${Number(r.count ?? 0)} (Δ ${deltaLabel})`);
      if (fmt(r.topDriver)) lines.push(`  - Top driver: ${fmt(r.topDriver)}`);
    }
  } else {
    lines.push('- (No risks)');
  }
  lines.push('');

  lines.push('## Strategic Bets (Portfolio)');
  if (Array.isArray(dailyBrief.bets) && dailyBrief.bets.length) {
    for (const b of dailyBrief.bets) {
      lines.push(`- ${fmt(b.name) || b.key} — ${String(b.rag ?? '').toUpperCase() || '—'}`);
      lines.push(`  - Timeline: ${Number(b.weeksNeeded ?? 0)}w needed / ${Number(b.weeksLeft ?? 0)}w left`);
      const drivers = Array.isArray(b.priorityDrivers) ? b.priorityDrivers : [];
      const driverParts = drivers
        .map((d) => {
          const label = fmt(d.label);
          const value = fmt(d.value);
          if (!label || !value) return null;
          const suffix =
            d.basis === 'assumption' ? ' (assumption)' : d.basis === 'configuration' ? ' (config)' : '';
          return `${label} ${value}${suffix}`;
        })
        .filter((p): p is string => Boolean(p));
      if (driverParts.length) lines.push(`  - Priority drivers: ${driverParts.join(' • ')}`);
      if (fmt(b.constraint)) lines.push(`  - Constraint: ${fmt(b.constraint)}`);
      if (fmt(b.accountability)) lines.push(`  - Accountability: ${fmt(b.accountability)}`);
      const options = Array.isArray(b.options) ? b.options : [];
      if (options.length) {
        lines.push('  - Options:');
        for (const o of options.slice(0, 3)) {
          lines.push(`    - ${fmt(o.action) || 'Option'} (${fmt(o.delta) || '—'})`);
        }
      }
      const proofs = Array.isArray(b.proofs) ? b.proofs : [];
      if (proofs.length) {
        lines.push('  - Proof anchors:');
        for (const p of proofs.slice(0, 3)) {
          lines.push(`    - ${fmt(p.signal) || 'Signal'} — ${fmt(p.detail) || '—'}`);
        }
      }
    }
  } else {
    lines.push('- (No bets loaded)');
  }
  lines.push('');

  lines.push('## Evidence Coverage');
  if (Array.isArray(dailyBrief.evidenceCoverage) && dailyBrief.evidenceCoverage.length) {
    for (const e of dailyBrief.evidenceCoverage) {
      const status = fmt(e.status) || '—';
      const source = fmt(e.sourceLabel) || '—';
      const detail = fmt(e.detail) || '';
      lines.push(`- ${fmt(e.label) || e.id}: ${status} (${source})${detail ? ` — ${detail}` : ''}`);
    }
  } else {
    lines.push('- (No evidence coverage reported)');
  }
  lines.push('');
  lines.push('---');
  lines.push('Generated from the approved Daily Brief run and decision package evidence.');
  return lines.join('\n');
}

function downloadMarkdown(filename: string, text: string) {
  const safeName = String(filename ?? '').trim() || 'helix-board-memo.md';
  const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = safeName;
  a.click();
  URL.revokeObjectURL(url);
}

function shouldLockWrites(message: string): boolean {
  const human = humanizeApiError(message);
  return human.title === 'Access denied' || human.title === 'Sign-in required';
}

function buildNoNewerWorkSignalsSnapshotMessage(params: {
  previousRunId: string | null;
  nextRunId: string | null;
  dailyBrief: ExecutionCockpitDailyBriefDto;
}): string | null {
  if (!params.previousRunId || !params.nextRunId || params.previousRunId !== params.nextRunId) {
    return null;
  }

  const workSignals = Array.isArray(params.dailyBrief.evidenceCoverage)
    ? params.dailyBrief.evidenceCoverage.find((item) => item.id === 'work_signals') ?? null
    : null;
  if (!workSignals || workSignals.status !== 'stale') {
    return null;
  }

  const sourceLabel = String(workSignals.sourceLabel ?? '').trim() || 'work-signal snapshot';
  const detail = String(workSignals.detail ?? '').trim();
  return `No newer work-signal snapshot is available yet. Daily Brief is still anchored to ${sourceLabel}${detail ? ` (${detail})` : ''}. Sync a fresher work-signal snapshot, then refresh again.`;
}

export default function DailyBriefClient() {
  const searchParams = useSearchParams();
  const e2eCase = searchParams.get('__case');
  const requestedStrategyId = searchParams.get('strategyId')?.trim() || null;
  const requestedScopeRootNodeId = searchParams.get('scopeRootNodeId')?.trim() || null;
  const forceMock = isE2eMode && e2eCase === 'mock';

  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isWriteLocked, setIsWriteLocked] = useState(false);
  const [data, setData] = useState<ExecutionCockpitDailyBriefDto | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [boardPackMd, setBoardPackMd] = useState<string | null>(null);
  const [meta, setMeta] = useState<ExecutionCockpitDailyBriefMetaDto | null>(null);
  const [ownerCandidates, setOwnerCandidates] = useState<DecisionOwnerCandidate[] | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isRefreshingSnapshot, setIsRefreshingSnapshot] = useState(false);
  const [decisionPackage, setDecisionPackage] = useState<ExecutionCockpitDecisionPackageDto | null>(null);
  const [decisionPackageDecisions, setDecisionPackageDecisions] = useState<ExecutionCockpitDecisionPackageDecisionDto[]>([]);
  const [isActivatingDecisionPackage, setIsActivatingDecisionPackage] = useState(false);
  const [isSavingDecisionPackageDraft, setIsSavingDecisionPackageDraft] = useState(false);
  const [isStartingDecisionPackageAmendment, setIsStartingDecisionPackageAmendment] = useState(false);
  const [isReviewingDecisionPackage, setIsReviewingDecisionPackage] = useState(false);
  const [resolvedStrategyId, setResolvedStrategyId] = useState<string | null>(null);
  const [resolvedStrategyTitle, setResolvedStrategyTitle] = useState<string | null>(null);
  const [resolvedScopeRootNodeId, setResolvedScopeRootNodeId] = useState<string | null>(null);

  const cockpitHref = useMemo(() => {
    if (!resolvedStrategyId) return null;
    return `/strategy-studio/strategies/${encodeURIComponent(resolvedStrategyId)}/execution-cockpit`;
  }, [resolvedStrategyId]);

  const askHelixTopSlot = (
    <div data-testid="daily-brief-ask-helix-card" className="rounded-lg border border-border bg-card p-4 text-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold text-foreground">Ask Helix</div>
          <div className="mt-1 text-xs text-muted-foreground">
            Ask Helix opens in the workspace — restate the question with the plan or run you mean.
            Evidence-linked where available. Assumptions are shown. No changes are applied without
            your approval.
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <AskHelixLink testId="daily-brief-ask-helix" />
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setError(null);
    setActionError(null);
    setIsWriteLocked(false);

    if (forceMock) {
      setStatus('ready');
      setData(dailyBriefV6Mock);
      setRunId('mock');
      setBoardPackMd(null);
      setMeta(null);
      setOwnerCandidates(null);
      setDecisionPackage(null);
      setDecisionPackageDecisions([]);
      return () => {
        cancelled = true;
      };
    }

    const resolveStrategy = async (): Promise<{
      id: string | null;
      title: string | null;
      scopeRootNodeId: string | null;
      coverageReady: boolean;
      reason: string | null;
    }> => {
      const operatingContext = await loadOperatingContext({
        strategyId: requestedStrategyId,
        scopeRootNodeId: requestedScopeRootNodeId,
      });
      const resolvedId = String(operatingContext.strategyId ?? '').trim() || null;
      const resolvedTitle = operatingContext.strategyTitle ?? null;
      const coverageReady = operatingContext.kind === 'ready';
      const resolvedScopeRootNodeId =
        operatingContext.kind === 'ready' && operatingContext.coverage.kind === 'business_area'
          ? operatingContext.coverage.scopeRootNodeId
          : null;
      const resolvedReason = operatingContext.kind === 'unavailable' ? operatingContext.reason : null;

      if (!resolvedId) {
        return {
          id: null,
          title: resolvedTitle,
          scopeRootNodeId: resolvedScopeRootNodeId,
          coverageReady,
          reason: resolvedReason ?? 'No active enterprise strategy is set.',
        };
      }

      try {
        window.localStorage.setItem(LAST_STRATEGY_KEY, resolvedId);
      } catch {
        // ignore
      }

      return {
        id: resolvedId,
        title: resolvedTitle,
        scopeRootNodeId: resolvedScopeRootNodeId,
        coverageReady,
        reason: resolvedReason,
      };
    };

    void (async () => {
      const resolved = await resolveStrategy();
      if (cancelled) return;
      if (!resolved.id) {
        setStatus('error');
        setError(resolved.reason ?? 'No active enterprise strategy is set.');
        return;
      }

      setResolvedStrategyId(resolved.id);
      setResolvedStrategyTitle(resolved.title);
      setResolvedScopeRootNodeId(resolved.scopeRootNodeId);

      void listTenantMembers()
        .then((members) => {
          if (cancelled) return;
          const candidates: DecisionOwnerCandidate[] = [];
          for (const member of Array.isArray(members) ? members : []) {
            const status = String(member?.membershipStatus ?? '').toLowerCase();
            if (status && status !== 'active') continue;
            const userId = String(member?.userId ?? '').trim();
            const email = String(member?.email ?? '').trim();
            if (!userId) continue;
            candidates.push({
              userId,
              email,
              label: String(member?.name ?? '').trim() || email || userId,
            });
          }
          candidates.sort((a, b) => a.label.localeCompare(b.label));
          setOwnerCandidates(candidates);
        })
        .catch(() => {
          if (cancelled) return;
          setOwnerCandidates(null);
        });

      const latestRun = await getLatestExecutionCockpitRun(
        resolved.id,
        resolved.scopeRootNodeId,
      );
      if (cancelled) return;

      if (!latestRun?.runId) {
        setStatus('error');
        setError('No execution run found yet. Configure Decision Console inputs for this strategy, then return to Daily Brief.');
        return;
      }

      const res = await getExecutionCockpitDailyBriefFromRun({
        runId: latestRun.runId,
        scopeRootNodeId: resolved.scopeRootNodeId,
        view: buildDefaultView(resolved.title),
      });
      if (cancelled) return;

      setData(res.dailyBrief);
      setRunId(res.runId);
      setBoardPackMd(res.boardPackMd ?? null);
      setMeta(res.meta ?? null);
      setDecisionPackage(res.decisionPackage ?? null);
      setDecisionPackageDecisions(res.decisionPackageDecisions ?? []);
      setIsWriteLocked(res.meta?.capabilities?.canWrite === false);
      setStatus('ready');
    })().catch((err: unknown) => {
      if (cancelled) return;
      const message = err instanceof Error ? err.message : 'Failed to load Daily Brief.';
      setStatus('error');
      setError(message);
      setData(null);
      setRunId(null);
      setBoardPackMd(null);
      setMeta(null);
      setOwnerCandidates(null);
      setDecisionPackage(null);
      setDecisionPackageDecisions([]);
    });

    return () => {
      cancelled = true;
    };
  }, [forceMock, requestedScopeRootNodeId, requestedStrategyId]);

  const handleRefreshSnapshot = useCallback(async () => {
    if (!resolvedStrategyId || isRefreshingSnapshot || isWriteLocked) return;
    setIsRefreshingSnapshot(true);
    setActionError(null);

    try {
      const previousRunId = runId;
      const latestRun = await ensureLatestExecutionCockpitRun(
        resolvedStrategyId,
        resolvedScopeRootNodeId,
      );
      if (!latestRun?.runId) {
        setStatus('error');
        setError('No execution run found yet. Configure Execution Cockpit inputs for this strategy, then return to Daily Brief.');
        return;
      }

      const res = await getExecutionCockpitDailyBriefFromRun({
        runId: latestRun.runId,
        scopeRootNodeId: resolvedScopeRootNodeId,
        view: (data?.filters as ExecutionCockpitDailyBriefViewDto | undefined) ?? buildDefaultView(resolvedStrategyTitle),
      });

      setData(res.dailyBrief);
      setRunId(res.runId);
      setBoardPackMd(res.boardPackMd ?? null);
      setMeta(res.meta ?? null);
      setDecisionPackage(res.decisionPackage ?? null);
      setDecisionPackageDecisions(res.decisionPackageDecisions ?? []);
      setIsWriteLocked(res.meta?.capabilities?.canWrite === false);
      setStatus('ready');

      const noChangeMessage = buildNoNewerWorkSignalsSnapshotMessage({
        previousRunId,
        nextRunId: res.runId,
        dailyBrief: res.dailyBrief,
      });
      if (noChangeMessage) {
        setActionError(noChangeMessage);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to refresh Daily Brief.';
      setActionError(message);
      if (shouldLockWrites(message)) setIsWriteLocked(true);
    } finally {
      setIsRefreshingSnapshot(false);
    }
  }, [data?.filters, isRefreshingSnapshot, isWriteLocked, resolvedScopeRootNodeId, resolvedStrategyId, resolvedStrategyTitle, runId]);

  const handleExport = useCallback(async () => {
    if (!data || !runId) return;
    setIsExporting(true);
    try {
      const text =
        (boardPackMd ?? '').trim()
          ? String(boardPackMd)
          : buildDeterministicBoardMemo({
              dailyBrief: data,
              runId,
              meta,
              decisionPackage,
              decisionPackageDecisions,
              ownerCandidates,
            });
      downloadMarkdown(`helix-board-memo-${runId}.md`, text);
    } finally {
      setIsExporting(false);
    }
  }, [boardPackMd, data, decisionPackage, decisionPackageDecisions, meta, ownerCandidates, runId]);

  const handleActivateDecisionPackage = useCallback(
    async (decisions: ExecutionCockpitDecisionPackageActivateRequestDto['decisions']) => {
      if (!runId || isActivatingDecisionPackage || isWriteLocked) return;
      setIsActivatingDecisionPackage(true);
      setActionError(null);
      try {
        const res = await activateExecutionCockpitDecisionPackageForRun({ runId, decisions });
        setDecisionPackage(res.decisionPackage ?? null);
        setDecisionPackageDecisions(res.decisions ?? []);

        const refreshed = await getExecutionCockpitDailyBriefFromRun({
          runId,
          scopeRootNodeId: resolvedScopeRootNodeId,
          view: (data?.filters as ExecutionCockpitDailyBriefViewDto | undefined) ?? buildDefaultView(resolvedStrategyTitle),
        });
        setData(refreshed.dailyBrief);
        setRunId(refreshed.runId);
        setBoardPackMd(refreshed.boardPackMd ?? null);
        setMeta(refreshed.meta ?? null);
        setDecisionPackage(refreshed.decisionPackage ?? res.decisionPackage ?? null);
        setDecisionPackageDecisions(refreshed.decisionPackageDecisions ?? res.decisions ?? []);
        setIsWriteLocked(refreshed.meta?.capabilities?.canWrite === false);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to activate decision package.';
        setActionError(message);
        if (shouldLockWrites(message)) setIsWriteLocked(true);
      } finally {
        setIsActivatingDecisionPackage(false);
      }
    },
    [data?.filters, isActivatingDecisionPackage, isWriteLocked, resolvedScopeRootNodeId, resolvedStrategyTitle, runId],
  );

  const handleSaveDecisionPackageDraft = useCallback(
    async (decisions: ExecutionCockpitDecisionPackageActivateRequestDto['decisions']) => {
      if (!runId || isSavingDecisionPackageDraft || isWriteLocked) return;
      setIsSavingDecisionPackageDraft(true);
      setActionError(null);
      try {
        const res = await saveExecutionCockpitDecisionPackageDraftForRun({ runId, decisions });
        setDecisionPackage(res.decisionPackage ?? null);
        setDecisionPackageDecisions(res.decisions ?? []);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to save decision package draft.';
        setActionError(message);
        if (shouldLockWrites(message)) setIsWriteLocked(true);
      } finally {
        setIsSavingDecisionPackageDraft(false);
      }
    },
    [isSavingDecisionPackageDraft, isWriteLocked, runId],
  );

  const handleStartDecisionPackageAmendment = useCallback(async () => {
    if (!runId || isStartingDecisionPackageAmendment || isWriteLocked) return;
    setIsStartingDecisionPackageAmendment(true);
    setActionError(null);
    try {
      const res = await startExecutionCockpitDecisionPackageAmendmentForRun(runId);
      setDecisionPackage(res.decisionPackage ?? null);
      setDecisionPackageDecisions(res.decisions ?? []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to start a decision package amendment.';
      setActionError(message);
      if (shouldLockWrites(message)) setIsWriteLocked(true);
    } finally {
      setIsStartingDecisionPackageAmendment(false);
    }
  }, [isStartingDecisionPackageAmendment, isWriteLocked, runId]);

  const handleReviewDecisionPackage = useCallback(
    async (review: ExecutionCockpitDecisionPackageReviewRequestDto) => {
      if (!runId || isReviewingDecisionPackage || isWriteLocked) return;
      setIsReviewingDecisionPackage(true);
      setActionError(null);
      try {
        await reviewExecutionCockpitDecisionPackageForRun({
          runId,
          initiativeKeys:
            Array.isArray(review?.initiativeKeys) && review.initiativeKeys.length ? review.initiativeKeys : undefined,
          outcome: review?.outcome ?? undefined,
          notes: review?.notes ?? undefined,
        });

        const res = await getExecutionCockpitDailyBriefFromRun({
          runId,
          scopeRootNodeId: resolvedScopeRootNodeId,
          view: (data?.filters as ExecutionCockpitDailyBriefViewDto | undefined) ?? buildDefaultView(resolvedStrategyTitle),
        });

        setData(res.dailyBrief);
        setRunId(res.runId);
        setBoardPackMd(res.boardPackMd ?? null);
        setMeta(res.meta ?? null);
        setDecisionPackage(res.decisionPackage ?? null);
        setDecisionPackageDecisions(res.decisionPackageDecisions ?? []);
        setIsWriteLocked(res.meta?.capabilities?.canWrite === false);
        setStatus('ready');
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to mark decision package reviewed.';
        setActionError(message);
        if (shouldLockWrites(message)) setIsWriteLocked(true);
      } finally {
        setIsReviewingDecisionPackage(false);
      }
    },
    [data?.filters, isReviewingDecisionPackage, isWriteLocked, resolvedScopeRootNodeId, resolvedStrategyTitle, runId],
  );

  const handleLoadDecisionPackageReviewHistory = useCallback(
    async (initiativeKey: string) => {
      if (!runId) throw new Error('Missing runId');
      return getExecutionCockpitDecisionPackageReviewHistoryForRun({ runId, initiativeKey, limit: 200 });
    },
    [runId],
  );

  const exportHandler = data && runId ? handleExport : undefined;

  return (
    <div data-testid="daily-brief-page" data-hydrated="true">
      {status === 'error' && error ? (
        <div className="mx-auto max-w-5xl px-6 pt-6">
          {(() => {
            const humanError = humanizeApiError(error);
            const detail =
              humanError.title === 'Unable to load' && typeof error === 'string' && error.trim()
                ? error
                : humanError.message;
            return (
              <div className="rounded-lg border border-border bg-card p-4 text-sm">
                <div className="font-semibold text-foreground">{humanError.title}</div>
                <div className="mt-1 text-muted-foreground">{detail}</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {cockpitHref ? <LinkButton href={cockpitHref}>Go to Execution Cockpit</LinkButton> : null}
                  <LinkButton variant="secondary" href="/strategy-studio/strategies">
                    Go to Strategies
                  </LinkButton>
                </div>
              </div>
            );
          })()}
        </div>
      ) : null}

      {status === 'loading' && !data ? (
        <div className="mx-auto max-w-5xl px-6 pt-6 text-xs text-muted-foreground">Loading Daily Brief…</div>
      ) : null}

      {status === 'ready' && actionError ? (
        <div className="mx-auto max-w-5xl px-6 pt-6">
          {(() => {
            const humanError = humanizeApiError(actionError);
            return (
              <div className="rounded-lg border border-border bg-card p-4 text-sm">
                <div className="font-semibold text-foreground">{humanError.title}</div>
                <div className="mt-1 text-muted-foreground">{humanError.message}</div>
              </div>
            );
          })()}
        </div>
      ) : null}

      {data && runId && status === 'ready' && data.bets.length === 0 ? (
        <div className="mx-auto max-w-5xl px-6 pt-6">
          <div className="rounded-lg border border-border bg-card p-4 text-sm">
            <div className="font-semibold text-foreground">Daily Brief has no tracked bets yet</div>
            <div className="mt-1 text-muted-foreground">
              Configure Decision Console initiatives for this strategy, run it once, then return here.
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {cockpitHref ? <LinkButton href={cockpitHref}>Open Execution Cockpit</LinkButton> : null}
              <LinkButton variant="secondary" href="/strategy-studio/strategies">
                Go to Strategies
              </LinkButton>
            </div>
          </div>
        </div>
      ) : null}

      {data && runId && data.bets.length > 0 ? (
        <div data-testid="daily-brief-main">
          <ExecutionCockpitDailyBriefV6
            key={`${runId}:${data.asOf}`}
            data={data}
            meta={meta}
            ownerCandidates={ownerCandidates}
            audience="exec"
            isWriteLocked={isWriteLocked}
            onExportBoardMemo={exportHandler}
            isExportingBoardMemo={isExporting}
            onRefreshSnapshot={handleRefreshSnapshot}
            isRefreshingSnapshot={isRefreshingSnapshot}
            decisionPackage={decisionPackage}
            decisionPackageDecisions={decisionPackageDecisions}
            onActivateDecisionPackage={handleActivateDecisionPackage}
            isActivatingDecisionPackage={isActivatingDecisionPackage}
            onSaveDecisionPackageDraft={handleSaveDecisionPackageDraft}
            isSavingDecisionPackageDraft={isSavingDecisionPackageDraft}
            onStartDecisionPackageAmendment={handleStartDecisionPackageAmendment}
            isStartingDecisionPackageAmendment={isStartingDecisionPackageAmendment}
            onReviewDecisionPackage={handleReviewDecisionPackage}
            isReviewingDecisionPackage={isReviewingDecisionPackage}
            onLoadDecisionPackageReviewHistory={handleLoadDecisionPackageReviewHistory}
            topSlot={askHelixTopSlot}
            links={{
              orgCanvas: resolvedStrategyId ? `/org-canvas?strategyId=${encodeURIComponent(resolvedStrategyId)}` : null,
              evidenceCoverage: '/admin/org-structure',
              executionCockpit: cockpitHref,
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
